import { Injectable, signal } from '@angular/core';
import { ToastType } from '../../ui-component/toast-container/toast-type.constant';

// Re-export for backwards compatibility
export { ToastType };

/** Last-resort title so a toast never renders blank. */
const DEFAULT_TOAST_TITLE = 'Something went wrong';

/**
 * Single toast notification message
 */
export interface Toast {
  id: number;
  type: ToastType;
  title: string;
  message?: string;
}

/**
 * Input accepted by showError / showSuccess.
 * Can be a plain string (used as the title) or an object with a `title`
 * and an optional `message` / `description` detail.
 */
export type ToastInput =
  | string
  | { title: string; message?: string; description?: string };

/**
 * ToastService
 *
 * Global toast service for displaying success and error messages app-wide.
 * Used throughout the application to provide user feedback.
 *
 * Toasts auto-dismiss after a configurable duration.
 *
 * ── Ownership rules ──────────────────────────────────────────────────────────
 * The backend owns all toast messages for API-triggered actions.
 * The following are the ONLY approved categories of frontend-owned toast messages
 * that may use hardcoded strings:
 *
 *   1. Clipboard actions — e.g. "Link copied to clipboard" (no API involved).
 *   2. 403 interceptor — the generic "no permission" redirect message that fires
 *      before the full response body is available.
 *   3. Pre-flight client-side validation — errors shown before an API call is
 *      made (e.g. "A table must contain at least one field.",
 *      "Invalid imported file id").
 *
 * All other success/error messages must come from response.message (success)
 * or extractApiError(err) with no hardcoded fallback (error).
 * ─────────────────────────────────────────────────────────────────────────────
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private nextId = 0;
  private readonly defaultDuration = 5000; // 5 seconds

  /** Active toasts (reactive signal for template binding) */
  readonly toasts = signal<Toast[]>([]);

  /**
   * Show an error toast.
   * Accepts a plain string title, or an object `{ title, message?, description? }`.
   */
  showError(titleOrError: ToastInput, message?: string): void {
    if (typeof titleOrError === 'string') {
      this.addToast(ToastType.ERROR, titleOrError, message);
    } else {
      this.addToast(
        ToastType.ERROR,
        titleOrError.title,
        titleOrError.message ?? titleOrError.description
      );
    }
  }

  /**
   * Show a success toast.
   * Accepts a plain string title, or an object `{ title, message?, description? }`.
   */
  showSuccess(titleOrSuccess: ToastInput, message?: string): void {
    if (typeof titleOrSuccess === 'string') {
      this.addToast(ToastType.SUCCESS, titleOrSuccess, message);
    } else {
      this.addToast(
        ToastType.SUCCESS,
        titleOrSuccess.title,
        titleOrSuccess.message ?? titleOrSuccess.description
      );
    }
  }

  /**
   * Dismiss a specific toast by id
   */
  dismiss(id: number): void {
    this.toasts.update(list => list.filter(n => n.id !== id));
  }

  /**
   * Clear all toasts
   */
  clearAll(): void {
    this.toasts.set([]);
  }

  /**
   * Show a toast from a backend API response or error.
   * On success (2xx), reads `response.message` and shows a success toast.
   * On error, reads `err.error?.message` and shows an error toast.
   * Falls back to `fallbackMessage` when the response has no message.
   */
  fromBackendResponse(
    response:
      | { message?: { title: string; description?: string } | null }
      | {
          error?: {
            message?: { title: string; description?: string } | string;
          };
        },
    fallbackMessage = 'Something went wrong. Please try again.'
  ): void {
    const msg =
      'message' in response && response.message ? response.message : null;

    const errBody = 'error' in response ? response.error : null;
    const errMsg =
      errBody && typeof errBody === 'object' && 'message' in errBody
        ? errBody.message
        : null;

    if (msg && typeof msg === 'object') {
      this.showSuccess(msg);
    } else if (errMsg && typeof errMsg === 'object') {
      this.showError(errMsg as { title: string; description?: string });
    } else if (errMsg && typeof errMsg === 'string') {
      this.showError(errMsg);
    } else {
      this.showError(fallbackMessage);
    }
  }

  /**
   * Add a toast and schedule auto-dismiss
   */
  private addToast(
    type: ToastType,
    title: string,
    message?: string,
    duration = this.defaultDuration
  ): void {
    const id = this.nextId++;
    const toast: Toast = {
      id,
      type,
      title: this.coerceText(title) || DEFAULT_TOAST_TITLE,
      message: this.coerceText(message) || undefined,
    };

    this.toasts.update(list => [...list, toast]);

    // Auto-dismiss
    setTimeout(() => this.dismiss(id), duration);
  }

  /**
   * Last line of defense against rendering a non-string (e.g. a structured
   * `{ title, description }` error object) directly in the template, which would
   * surface as the literal "[object Object]". Strings pass through untouched;
   * objects are unwrapped to their `title`/`description`/`message`; anything
   * else becomes an empty string rather than a broken stringification.
   */
  private coerceText(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      for (const key of ['title', 'description', 'message']) {
        if (typeof record[key] === 'string' && record[key]) {
          return record[key] as string;
        }
      }
      return '';
    }
    return value == null ? '' : String(value);
  }
}
